import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { trackEvent, trackPageView } from '../utils/analytics';
import Footer from './Footer';

const PRESENTS_URL = 'https://www.voxxypresents.com';

// Custom hook for scroll reveal animations
const useScrollReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isVisible];
};

/* ── Layout ── */

const Page = styled.main`
  background: var(--color-space-900);
  color: var(--color-text-primary);
`;

const Section = styled.section`
  padding: 4rem 1.5rem;
  position: relative;

  @media (min-width: 768px) {
    padding: 5rem 1.5rem;
  }

  @media (min-width: 1024px) {
    padding: 6rem 1.5rem;
  }

  ${({ $variant }) =>
    $variant === 'alt'
      ? css`
          background: linear-gradient(160deg, rgba(16, 10, 32, 0.92), rgba(27, 18, 47, 0.88));
        `
      : null}

  ${({ $isHero }) =>
    $isHero &&
    css`
      min-height: 100vh;
      display: flex;
      align-items: center;
      padding: 1.5rem 1.5rem;

      @media (min-width: 768px) {
        padding-top: 0;
        padding-bottom: 0;
      }
    `}

  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transform: translateY(${({ $isVisible }) => ($isVisible ? '0' : '40px')});
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
`;

const SectionInner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  display: grid;
  gap: 2.5rem;

  @media (min-width: 768px) {
    gap: 3rem;
  }

  @media (min-width: 1024px) {
    gap: 4rem;
  }
`;

const Split = styled.div`
  display: grid;
  gap: 1rem;
  align-items: center;

  @media (min-width: 480px) {
    gap: 1.5rem;
  }

  @media (min-width: 768px) {
    gap: 2.5rem;
  }

  @media (min-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 3rem;
  }

  ${({ $reverseOnMobile }) =>
    $reverseOnMobile &&
    css`
      @media (max-width: 959px) {
        > *:first-child {
          order: 2;
        }
        > *:last-child {
          order: 1;
        }
      }
    `}
`;

/* ── Typography ── */

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--letter-ultra-wide);
  color: var(--color-text-muted);
  margin: auto;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: 2rem;
  line-height: 1.1;
  margin: 0;
  text-wrap: balance;
  padding-top: 0.5rem;

  @media (min-width: 480px) {
    font-size: 2.5rem;
    padding-top: 0.75rem;
  }

  @media (min-width: 768px) {
    font-size: 3.5rem;
    line-height: 1.05;
    padding-top: 0;
  }

  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

const Heading = styled.h2`
  font-family: var(--font-display);
  font-size: 2rem;
  line-height: 1.1;
  margin: 0;
  text-wrap: balance;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 3rem;
  }
`;

const Subheading = styled.h3`
  font-family: var(--font-display);
  font-size: 1.5rem;
  line-height: 1.2;
  margin: 0;
  text-wrap: balance;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const Paragraph = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-text-secondary);
  max-width: 60ch;

  @media (min-width: 480px) {
    font-size: 1.05rem;
    line-height: 1.6;
  }

  @media (min-width: 768px) {
    font-size: 1.15rem;
    line-height: 1.65;
  }

  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const BodyCopy = styled.div`
  display: grid;
  gap: 0.75rem;
  text-align: center;

  @media (min-width: 480px) {
    gap: 1rem;
  }

  @media (min-width: 768px) {
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    gap: 1.8rem;
  }

  &.voxxy-surface {
    text-align: left;
  }
`;

/* ── Buttons ── */

const ButtonRow = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin: auto;

  @media (min-width: 480px) {
    gap: 0.75rem;
  }

  @media (min-width: 768px) {
    gap: 0.9rem;
  }

  ${({ $centered }) =>
    $centered &&
    css`
      justify-content: center;
    `}
`;

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  @media (min-width: 480px) {
    padding: 0.85rem 1.7rem;
    font-size: 1rem;
  }

  @media (min-width: 768px) {
    padding: 0.95rem 1.9rem;
  }
`;

const PrimaryButtonExternal = styled.a`
  ${buttonStyles}
  border: none;
  color: #ffffff;
  background-image: linear-gradient(120deg, #6a36ff 0%, #ff36d5 52%, #ff9d3f 100%);
  box-shadow: 0 12px 35px rgba(146, 77, 255, 0.35);

  &:hover {
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(146, 77, 255, 0.45);
  }
`;

const SecondaryButtonExternal = styled.a`
  ${buttonStyles}
  border: 1px solid rgba(203, 184, 255, 0.4);
  color: var(--color-plasma-300);
  background: rgba(255, 255, 255, 0.03);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(86, 51, 139, 0.35);
  }
`;

/* ── Cards & Lists ── */

const CardGrid = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(203, 184, 255, 0.12);
  border-radius: 20px;
  padding: 2rem;
  display: grid;
  gap: 0.75rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(146, 77, 255, 0.15);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  line-height: 1;
`;

const CardTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
`;

const CardText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 0;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.75rem;
  text-align: left;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--color-text-secondary);

  &:before {
    content: '';
    flex: none;
    margin-top: 0.55rem;
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(146, 77, 255, 0.8), rgba(255, 87, 208, 0.7));
    box-shadow: 0 0 12px rgba(146, 77, 255, 0.45);
  }
`;

/* ── Steps ── */

const StepsGrid = styled.div`
  display: grid;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

const Step = styled.div`
  text-align: center;
  display: grid;
  gap: 0.75rem;
  justify-items: center;
`;

const StepNumber = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6a36ff 0%, #ff36d5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.25rem;
  color: #fff;
`;

const StepTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
`;

const StepText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 0;
  max-width: 30ch;
`;

/* ── CTA Container ── */

const CTAContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  border-radius: 40px;
  padding: 2.8rem;
  background: linear-gradient(135deg, rgba(21, 5, 42, 0.95), rgba(59, 13, 88, 0.92));
  box-shadow: 0 35px 60px rgba(10, 3, 25, 0.55);
  display: grid;
  gap: 1.4rem;
  justify-items: center;
  text-align: center;

  @media (min-width: 768px) {
    padding: 3.5rem;
    gap: 1.7rem;
  }

  @media (min-width: 1024px) {
    padding: 4.5rem;
    gap: 2rem;
  }
`;

/* ══════════════════════════════════════
   SECTIONS
   ══════════════════════════════════════ */

const Hero = ({ onCtaClick }) => {
  return (
    <Section className="voxxy-aurora" $isVisible={true} $isHero={true}>
      <SectionInner>
        <BodyCopy>
          <Eyebrow>Event management software</Eyebrow>
          <Title className="voxxy-title--glow">
            The operating system for recurring event producers.
          </Title>
          <Paragraph style={{ margin: '0 auto' }}>
            Manage vendor applications, automate communications, and grow your events — art markets, pop-ups, craft fairs, and farmers markets — all from one platform.
          </Paragraph>
          <ButtonRow $centered>
            <PrimaryButtonExternal
              href={PRESENTS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCtaClick}
            >
              Get Started Free
            </PrimaryButtonExternal>
            <SecondaryButtonExternal
              href={`${PRESENTS_URL}/#contact`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCtaClick}
            >
              Request a Demo
            </SecondaryButtonExternal>
          </ButtonRow>
        </BodyCopy>
      </SectionInner>
    </Section>
  );
};

const ProblemSection = ({ onCtaClick }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Section ref={ref} $isVisible={isVisible}>
      <SectionInner>
        <BodyCopy>
          <Eyebrow>The problem</Eyebrow>
          <Heading>Running recurring events shouldn't feel this hard.</Heading>
          <Paragraph style={{ margin: '0 auto' }}>
            Most event producers piece things together with spreadsheets, group chats, and scattered emails. It works — until it doesn't.
          </Paragraph>
        </BodyCopy>

        <CardGrid>
          <Card>
            <CardIcon>📋</CardIcon>
            <CardTitle>Scattered tools</CardTitle>
            <CardText>
              Vendor lists in spreadsheets, payments in Venmo, updates in group chats. Nothing talks to anything else.
            </CardText>
          </Card>
          <Card>
            <CardIcon>⏰</CardIcon>
            <CardTitle>Unpaid coordination</CardTitle>
            <CardText>
              Hours spent chasing vendor confirmations, collecting fees, and sending repetitive emails between each edition.
            </CardText>
          </Card>
          <Card>
            <CardIcon>📂</CardIcon>
            <CardTitle>No vendor CRM</CardTitle>
            <CardText>
              Vendor history, preferences, and contact info live in your head. Scaling means starting from scratch every time.
            </CardText>
          </Card>
        </CardGrid>
      </SectionInner>
    </Section>
  );
};

const FeaturesSection = ({ onCtaClick }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Section ref={ref} $isVisible={isVisible} $variant="alt">
      <SectionInner>
        <BodyCopy>
          <Eyebrow>Everything you need</Eyebrow>
          <Heading>One platform. Every edition.</Heading>
        </BodyCopy>

        <Split>
          <BodyCopy className="voxxy-surface voxxy-surface--spacious">
            <Subheading className="voxxy-title">Vendor management</Subheading>
            <Paragraph>
              Accept applications, communicate with vendors, and manage your roster from one dashboard. No more spreadsheets and scattered emails between editions.
            </Paragraph>
            <List>
              <ListItem>Vendor applications and approvals</ListItem>
              <ListItem>Automated vendor communication</ListItem>
              <ListItem>Roster management across editions</ListItem>
              <ListItem>Payment and fee tracking</ListItem>
            </List>
          </BodyCopy>
          <BodyCopy className="voxxy-surface voxxy-surface--spacious">
            <Subheading className="voxxy-title">Event operations</Subheading>
            <Paragraph>
              Build reusable event templates, track budgets, and manage every detail. Whether it's a monthly art market or a weekly farmers market, Voxxy keeps your ops running smoothly.
            </Paragraph>
            <List>
              <ListItem>Reusable event templates and scheduling</ListItem>
              <ListItem>Budget tracking and financial tools</ListItem>
              <ListItem>Attendee communication and updates</ListItem>
              <ListItem>Public event pages for promotion</ListItem>
            </List>
          </BodyCopy>
        </Split>

        <ButtonRow $centered>
          <PrimaryButtonExternal
            href={PRESENTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
          >
            Start Managing Events
          </PrimaryButtonExternal>
        </ButtonRow>
      </SectionInner>
    </Section>
  );
};

const HowItWorks = ({ onCtaClick }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Section ref={ref} $isVisible={isVisible}>
      <SectionInner>
        <BodyCopy>
          <Eyebrow>How it works</Eyebrow>
          <Heading>Up and running in minutes.</Heading>
        </BodyCopy>

        <StepsGrid>
          <Step>
            <StepNumber>1</StepNumber>
            <StepTitle>Create your event</StepTitle>
            <StepText>
              Set up your recurring event with dates, vendor categories, and booth details.
            </StepText>
          </Step>
          <Step>
            <StepNumber>2</StepNumber>
            <StepTitle>Open applications</StepTitle>
            <StepText>
              Share your public event page. Vendors apply directly — no more back-and-forth emails.
            </StepText>
          </Step>
          <Step>
            <StepNumber>3</StepNumber>
            <StepTitle>Coordinate & grow</StepTitle>
            <StepText>
              Approve vendors, send updates, track payments, and build your community edition after edition.
            </StepText>
          </Step>
        </StepsGrid>

        <ButtonRow $centered>
          <PrimaryButtonExternal
            href={PRESENTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
          >
            Try Voxxy Free
          </PrimaryButtonExternal>
        </ButtonRow>
      </SectionInner>
    </Section>
  );
};

const FinalCTA = ({ onCtaClick }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <Section ref={ref} $isVisible={isVisible}>
      <CTAContainer>
        <Heading>Ready to run your next event with Voxxy?</Heading>
        <Paragraph>
          Join producers who use Voxxy to manage vendors, track budgets, and grow their recurring events.
        </Paragraph>
        <ButtonRow $centered>
          <PrimaryButtonExternal
            href={PRESENTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
          >
            Get Started Free
          </PrimaryButtonExternal>
          <SecondaryButtonExternal
            href={`${PRESENTS_URL}/#contact`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onCtaClick}
          >
            Contact Us
          </SecondaryButtonExternal>
        </ButtonRow>
      </CTAContainer>
    </Section>
  );
};

/* ══════════════════════════════════════
   PAGE
   ══════════════════════════════════════ */

const LandingPage = () => {
  useEffect(() => {
    // Redirect all visitors to Voxxy Presents
    window.location.replace(PRESENTS_URL);
  }, []);

  // Show nothing while redirecting
  return null;
};

export default LandingPage;
